import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const GOOGLE_ADS_QUOTE_CONVERSION = 'AW-16614537746/hTCFCPLsj7wcEJL0tvI9';

const trackQuoteRequestConversion = (email) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'quote_request_submitted' });

  if (typeof window.gtag !== 'function') return;

  const normalizedEmail = email?.trim().toLowerCase();
  if (normalizedEmail) {
    window.gtag('set', 'user_data', { email: normalizedEmail });
  }

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_QUOTE_CONVERSION,
  });
};

const TrademarkSearch = () => {
  const [formData, setFormData] = useState({
    trademark: '',
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    class: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.trademark || !formData.name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formData.email || '').trim())) {
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      // Configuração do EmailJS
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Debug: verificar variáveis (remover em produção se necessário)
      console.log('EmailJS Config:', {
        hasServiceId: !!serviceId,
        hasTemplateId: !!templateId,
        hasPublicKey: !!publicKey
      });

      // Validar se as variáveis de ambiente estão configuradas
      if (!serviceId || !templateId || !publicKey) {
        const errorMsg = 'EmailJS não está configurado. Por favor, configure as variáveis de ambiente REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID e REACT_APP_EMAILJS_PUBLIC_KEY no Vercel ou no arquivo .env.local. Veja EMAILJS_SETUP.md para instruções.';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Inicializar EmailJS
      emailjs.init(publicKey);

      // Preparar parâmetros do template
      // IMPORTANTE: Os nomes das variáveis devem corresponder exatamente ao template no EmailJS
      const templateParams = {
        to_email: 'tmrush.br@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        trademark: formData.trademark,
        phone: formData.phone ? `${formData.countryCode} ${formData.phone}` : 'Não informado',
        class: formData.class || 'Não selecionada',
        description: formData.description || 'Não fornecida',
        reply_to: formData.email,
      };

      console.log('Enviando email com parâmetros:', { serviceId, templateId, templateParams });

      const response = await emailjs.send(serviceId, templateId, templateParams);
      
      console.log('Resposta do EmailJS:', response);
      
      if (response.status === 200) {
        trackQuoteRequestConversion(formData.email);
        setSubmitted(true);
        setLoading(false);
      } else {
        throw new Error(`Erro ao enviar email. Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setError(true);
      setLoading(false);
      
      // Mensagem de erro mais específica
      let errorMessage = 'Erro ao enviar o formulário. ';
      if (error.message && error.message.includes('EmailJS não está configurado')) {
        errorMessage += 'Por favor, configure o EmailJS seguindo as instruções em EMAILJS_SETUP.md';
      } else if (error.message && error.message.includes('400')) {
        errorMessage += 'Verifique se o template do EmailJS está configurado corretamente com as variáveis: from_name, from_email, trademark, phone, class, description.';
      } else {
        errorMessage += 'Por favor, tente novamente ou entre em contato diretamente em contact@tmrush.com';
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div className="pt-40 pb-24 min-h-screen bg-background py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
          
            <h1 className="text-4xl md:text-6xl font-bold text-text mb-4">
            Request a Brazil Trademark Proposal
            </h1>
            <p className="text-xl text-text/70 mb-4">
            Tell us about your trademark and intended goods or services. We will review your inquiry and recommend the appropriate search or filing scope, with a clear quotation before work begins.
            </p>
            <p className="text-lg text-text/60">
            Join 14,000+ trademarks filed by the Marcas Já group.
            </p>
          </div>

          {/* Form */}
          {!submitted ? (
            <div className="bg-background-white rounded-3xl shadow-lg p-8 md:p-12 border border-gray-100/50">
              <h3 className="text-2xl md:text-3xl font-bold text-text mb-2">
              Tell us about your trademark
              </h3>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  Please fill in all required fields and a valid email address.
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    Trademark Name / Word Mark *
                  </label>
                  <input
                    type="text"
                    name="trademark"
                    value={formData.trademark}
                    onChange={handleChange}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your trademark"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    Attorney / Contact Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    Professional Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    Direct Phone / WhatsApp
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                    >
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+31">🇳🇱 +31</option>
                      <option value="+32">🇧🇪 +32</option>
                      <option value="+41">🇨🇭 +41</option>
                      <option value="+351">🇵🇹 +351</option>
                      <option value="+7">🇷🇺 +7</option>
                      <option value="+86">🇨🇳 +86</option>
                      <option value="+81">🇯🇵 +81</option>
                      <option value="+82">🇰🇷 +82</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+64">🇳🇿 +64</option>
                      <option value="+27">🇿🇦 +27</option>
                      <option value="+52">🇲🇽 +52</option>
                      <option value="+54">🇦🇷 +54</option>
                      <option value="+56">🇨🇱 +56</option>
                      <option value="+57">🇨🇴 +57</option>
                      <option value="+51">🇵🇪 +51</option>
                      <option value="+593">🇪🇨 +593</option>
                      <option value="+58">🇻🇪 +58</option>
                      <option value="+506">🇨🇷 +506</option>
                      <option value="+507">🇵🇦 +507</option>
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    Nice Class (If known)
                  </label>
                  <select
                    name="class"
                    value={formData.class}
                    onChange={handleChange}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Select a class (optional)</option>
                    <option value="1">Class 1 - Chemicals</option>
                    <option value="2">Class 2 - Paints and varnishes</option>
                    <option value="3">Class 3 - Cosmetics and cleaning preparations</option>
                    <option value="4">Class 4 - Lubricants and fuels</option>
                    <option value="5">Class 5 - Pharmaceuticals and medical preparations</option>
                    <option value="6">Class 6 - Common metals and alloys</option>
                    <option value="7">Class 7 - Machines and tools</option>
                    <option value="8">Class 8 - Hand tools and cutlery</option>
                    <option value="9">Class 9 - Computers, software and scientific instruments</option>
                    <option value="10">Class 10 - Medical and surgical instruments</option>
                    <option value="11">Class 11 - Lighting and heating apparatus</option>
                    <option value="12">Class 12 - Vehicles</option>
                    <option value="13">Class 13 - Firearms and explosives</option>
                    <option value="14">Class 14 - Jewelry and precious metals</option>
                    <option value="15">Class 15 - Musical instruments</option>
                    <option value="16">Class 16 - Paper and printed matter</option>
                    <option value="17">Class 17 - Rubber and plastics</option>
                    <option value="18">Class 18 - Leather goods and bags</option>
                    <option value="19">Class 19 - Building materials</option>
                    <option value="20">Class 20 - Furniture</option>
                    <option value="21">Class 21 - Household utensils</option>
                    <option value="22">Class 22 - Ropes and textile materials</option>
                    <option value="23">Class 23 - Yarns and threads</option>
                    <option value="24">Class 24 - Textiles and fabrics</option>
                    <option value="25">Class 25 - Clothing and footwear</option>
                    <option value="26">Class 26 - Lace and embroidery</option>
                    <option value="27">Class 27 - Carpets and floor coverings</option>
                    <option value="28">Class 28 - Games and sporting articles</option>
                    <option value="29">Class 29 - Meat and dairy products</option>
                    <option value="30">Class 30 - Coffee, tea and foodstuffs</option>
                    <option value="31">Class 31 - Agricultural products</option>
                    <option value="32">Class 32 - Non-alcoholic beverages</option>
                    <option value="33">Class 33 - Alcoholic beverages</option>
                    <option value="34">Class 34 - Tobacco products</option>
                    <option value="35">Class 35 - Advertising and business services</option>
                    <option value="36">Class 36 - Insurance and financial services</option>
                    <option value="37">Class 37 - Construction and repair services</option>
                    <option value="38">Class 38 - Telecommunications</option>
                    <option value="39">Class 39 - Transport and storage</option>
                    <option value="40">Class 40 - Material treatment services</option>
                    <option value="41">Class 41 - Education and entertainment</option>
                    <option value="42">Class 42 - Scientific and technological services</option>
                    <option value="43">Class 43 - Food and accommodation services</option>
                    <option value="44">Class 44 - Medical and beauty services</option>
                    <option value="45">Class 45 - Legal and security services</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-text mb-2">
                    List of Goods/Services
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Describe the products or services protected by this mark..."
                  />
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <p className="text-sm text-text/80 leading-relaxed">
                    <strong className="text-text">Confidentiality.</strong> Information submitted through this form is used to review and respond to your inquiry and may be processed by the technical providers supporting our contact workflow. Submission does not create a binding service engagement; an accepted quotation and advance payment are required before work begins.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white px-8 py-4 rounded-xl hover:bg-primary-hover font-semibold shadow-md hover:shadow-lg text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Request Filing Quote'}
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-background-white rounded-3xl shadow-lg p-12 text-center">
              <div className="text-6xl mb-6">✓</div>
              <h2 className="text-3xl font-bold text-text mb-4">
              Quote Request Received.
              </h2>
              <p className="text-lg text-text/70 mb-8">
              We have received your filing details. Our team is currently reviewing the trademark classes to prepare a Formal Proposal & Viability Report. You will receive it in your email within 24 business hours. Please keep an eye on your inbox (and spam folder) for an email from contact@tmrush.com.
              </p>
              <Link
                to="/"
                className="text-primary hover:text-primary-hover underline"
              >
                Back to Home
              </Link>
            </div>
          )}

          {/* FAQ. Our Team section lives on /about */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-text mb-8 text-center">
              Frequently asked questions
            </h3>
            <p className="text-center text-text/70 mb-12">
              Key details about our workflow, compliance, and partnership model.
            </p>

            <div className="space-y-6">
              <div className="bg-background-white rounded-2xl p-6 shadow-sm border border-gray-100/50">
                <h4 className="text-xl font-bold text-text mb-3">
                  1. Do I need a legalized Power of Attorney (POA) to file in Brazil?
                </h4>
                <p className="text-text/70 leading-relaxed">
                  No legalization required. Unlike many jurisdictions, the Brazilian INPI accepts a simply signed Power of Attorney (scanned PDF). No notarization or apostille is needed at the filing stage. We provide a bilingual draft for your client to sign, ensuring a friction-less onboarding process.
                </p>
              </div>

              <div className="bg-background-white rounded-2xl p-6 shadow-sm border border-gray-100/50">
                <h4 className="text-xl font-bold text-text mb-3">
                  2. How does 24h Filing work?
                </h4>
                <p className="text-text/70 leading-relaxed">
                  We target filing within 24 Brazilian business hours after complete approved instructions, the required signed POA and documents, and full cleared advance payment. We then promptly provide the BPTO filing receipt and application number.
                </p>
              </div>

              <div className="bg-background-white rounded-2xl p-6 shadow-sm border border-gray-100/50">
                <h4 className="text-xl font-bold text-text mb-3">
                  3. What happens if the trademark faces an opposition or refusal?
                </h4>
                <p className="text-text/70 leading-relaxed">
                  We defend your client&apos;s interests. TM RUSH® is not just a filing machine; we are a full-service IP firm backed by Marcas Já. If an Office Action or Opposition arises, our IP team will notify you immediately with a strategic analysis and a quote for the defense, ensuring end-to-end protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrademarkSearch;
